import React, { useState, useEffect } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Home, MessageSquare } from "lucide-react";
import { Sheet, SheetTrigger } from "@/components/ui/sheet";
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';
import CustomPortal from './CustomPortal';

// Import components
import HomeTab from "./j-chat/HomeTab";
import MessagingTab from "./j-chat/MessagingTab";
import Header from './j-chat/Header';

// Import config
import { CHAT_CONFIG } from './j-chat/config';

// Message type definition
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  lastUpdated: Date;
  metadata?: {
    tags?: string[];
    status?: 'active' | 'archived' | 'deleted';
    source?: string;
  };
}

const STORAGE_KEY = CHAT_CONFIG.STORAGE_KEY;

const JChatBot: React.FC = () => {
  console.log('RENDERING NEW CHATBOT COMPONENT: src/components/chat/JChatBot.tsx');
  
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>("home");
  const [userName, setUserName] = useState<string>("User");
  const [isOpen, setIsOpen] = useState(false);
  const [hideBottomTabs, setHideBottomTabs] = useState(false);
  const [shouldStartNewConversation, setShouldStartNewConversation] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) return [];
      
      const parsed = JSON.parse(saved);
      return parsed.map((conv: any) => ({
        ...conv,
        lastUpdated: new Date(conv.lastUpdated),
        messages: conv.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }))
      }));
    } catch (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
  });
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const { toast } = useToast();
  const [userId, setUserId] = useState<string | null>(null);

  // Save conversations to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
    } catch (error) {
      console.error('Error saving conversations:', error);
    }
  }, [conversations]);

  // Get user ID directly from Supabase when component mounts or auth changes
  useEffect(() => {
    const getUserId = async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        
        if (error) {
          console.error('Error getting auth user:', error);
          return;
        }
        
        if (data.user) {
          setUserId(data.user.id);
          console.log('Set user ID from Supabase auth:', data.user.id);
        }
      } catch (error) {
        console.error('Error in getUserId:', error);
      }
    };
    
    getUserId();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event);
      if (session?.user) {
        setUserId(session.user.id);
        console.log('Updated user ID from auth change:', session.user.id);
      } else {
        setUserId(null);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Fetch user's actual name from the database
  useEffect(() => {
    const fetchUserName = async () => {
      if (!userId) {
        console.log('No userId available for fetching user name');
        return;
      }
      
      try {
        console.log('Fetching user name for userId:', userId);
        const { data: profile, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('id', userId)
          .single();
        
        if (error) {
          console.error('Error fetching user profile:', error);
          // Fallback: try to get name from auth user metadata
          try {
            const { data: authData } = await supabase.auth.getUser();
            if (authData.user) {
              const fallbackName = authData.user.user_metadata?.given_name || 
                                 authData.user.user_metadata?.name?.split(' ')[0] ||
                                 authData.user.email?.split('@')[0] ||
                                 'User';
              console.log('Using fallback name from auth metadata:', fallbackName);
              setUserName(fallbackName);
            }
          } catch (authError) {
            console.error('Error getting auth user for fallback:', authError);
          }
          return;
        }
        
        console.log('Profile data received:', profile);
        
        if (profile) {
          if (profile.first_name) {
            console.log('Setting userName to first_name:', profile.first_name);
            setUserName(profile.first_name);
          } else {
            // Fallback to email username or auth metadata
            console.log('No first_name in profile, trying fallbacks...');
            try {
              const { data: authData } = await supabase.auth.getUser();
              if (authData.user) {
                const fallbackName = authData.user.user_metadata?.given_name || 
                                   authData.user.user_metadata?.name?.split(' ')[0] ||
                                   profile.email?.split('@')[0] ||
                                   authData.user.email?.split('@')[0] ||
                                   'User';
                console.log('Using fallback name:', fallbackName);
                setUserName(fallbackName);
              }
            } catch (authError) {
              console.error('Error getting auth user for fallback:', authError);
              setUserName('User');
            }
          }
        } else {
          console.log('No profile data found');
          setUserName('User');
        }
      } catch (error) {
        console.error('Error in fetchUserName:', error);
        setUserName('User');
      }
    };
    
    fetchUserName();
  }, [userId]);

  const updateConversation = (conversationId: string, messages: Message[]) => {
    // Only update if we have both user and bot messages
    const hasUserMessage = messages.some(m => m.sender === 'user');
    const hasBotMessage = messages.some(m => m.sender === 'bot');
    
    if (!hasUserMessage || !hasBotMessage) {
      console.log('Skipping conversation update - no user/bot interaction yet');
      return;
    }

    setConversations(prevConversations => {
      return prevConversations.map(conv => {
        if (conv.id === conversationId) {
          // Set title based on first user message if not already set
          let title = conv.title;
          if (!title && messages.length > 0) {
            const firstUserMessage = messages.find(m => m.sender === 'user');
            if (firstUserMessage) {
              title = firstUserMessage.text.slice(0, 50) + (firstUserMessage.text.length > 50 ? '...' : '');
            }
          }

          return {
            ...conv,
            title,
            messages,
            lastUpdated: new Date(),
            metadata: {
              ...conv.metadata,
              status: 'active' as const
            }
          };
        }
        return conv;
      });
    });
  };

  const createNewConversation = (initialMessage: string, messages: Message[]) => {
    // Only create conversation if we have both user and bot messages
    const hasUserMessage = messages.some(m => m.sender === 'user');
    const hasBotMessage = messages.some(m => m.sender === 'bot');
    
    if (!hasUserMessage || !hasBotMessage) {
      console.log('Skipping conversation creation - no user/bot interaction yet');
      return null;
    }

    const newConversation: Conversation = {
      id: uuidv4(),
      title: initialMessage.slice(0, 50) + (initialMessage.length > 50 ? '...' : ''),
      messages,
      lastUpdated: new Date(),
      metadata: {
        status: 'active',
        tags: ['new']
      }
    };

    setConversations(prev => [newConversation, ...prev]);
    return newConversation.id;
  };

  const sendMessageToWebhook = async (userMessage: string): Promise<{
    role: string;
    content: string;
  }> => {
    const webhookUrl = CHAT_CONFIG.API_URL;
    
    if (!webhookUrl) {
      console.error("Webhook URL is not configured");
      return {
        role: "assistant",
        content: t('chatbot.errors.notConfigured'),
      };
    }
    
    try {
      console.log("Sending message to webhook:", webhookUrl);
      
      // Generate a session ID if one doesn't exist in local storage
      let sessionId = localStorage.getItem(CHAT_CONFIG.SESSION_ID_KEY);
      if (!sessionId) {
        sessionId = uuidv4();
        localStorage.setItem(CHAT_CONFIG.SESSION_ID_KEY, sessionId);
      }
      
      // Try different methods to get the user ID in case the component's state doesn't have it
      let currentUserId = userId;
      
      // If we still don't have a user ID, try to get it directly
      if (!currentUserId) {
        try {
          // Try to get it directly from Supabase
          const { data } = await supabase.auth.getUser();
          if (data.user) {
            currentUserId = data.user.id;
            console.log('Got user ID directly from Supabase:', currentUserId);
          }
        } catch (error) {
          console.warn('Error getting user ID from Supabase:', error);
        }
      }
      
      // Fallback to storage methods
      if (!currentUserId) {
        try {
          // Method 1: Check for user ID in Supabase auth data
          const storedAuth = localStorage.getItem(CHAT_CONFIG.AUTH_KEY);
          if (storedAuth) {
            const parsedAuth = JSON.parse(storedAuth);
            if (parsedAuth && parsedAuth.user && parsedAuth.user.id) {
              currentUserId = parsedAuth.user.id;
              console.log('Found user ID in sb-auth:', currentUserId);
            }
          }
          
          // Method 2: Check for user ID in supabase.auth.session()
          const supabaseSession = localStorage.getItem('supabase.auth.token');
          if (!currentUserId) {
            if (supabaseSession) {
              try {
                const parsedSession = JSON.parse(supabaseSession);
                if (parsedSession && parsedSession.currentSession && parsedSession.currentSession.user && parsedSession.currentSession.user.id) {
                  currentUserId = parsedSession.currentSession.user.id;
                  console.log('Found user ID in supabase.auth.token:', currentUserId);
                }
              } catch (error) {
                console.warn('Error parsing supabase.auth.token', error);
              }
            }
          }
          
          // Method 3: Check for alternate user ID storage
          if (!currentUserId) {
            // Try getting user ID from profile
            const profileData = localStorage.getItem('user_profile');
            if (profileData) {
              try {
                const profile = JSON.parse(profileData);
                if (profile && profile.id) {
                  currentUserId = profile.id;
                  console.log('Found user ID in user_profile:', currentUserId);
                }
              } catch (error) {
                console.warn('Error parsing user_profile', error);
              }
            }
          }
          
          // Method 4: Check session storage as well
          if (!currentUserId) {
            const sessionAuth = sessionStorage.getItem(CHAT_CONFIG.AUTH_KEY);
            if (sessionAuth) {
              try {
                const parsedSessionAuth = JSON.parse(sessionAuth);
                if (parsedSessionAuth && parsedSessionAuth.user && parsedSessionAuth.user.id) {
                  currentUserId = parsedSessionAuth.user.id;
                  console.log('Found user ID in session storage:', currentUserId);
                }
              } catch (error) {
                console.warn('Error parsing session storage auth', error);
              }
            }
          }
          
          // Log all local storage keys to help debug
          console.log('Available localStorage keys:', Object.keys(localStorage));
          
        } catch (error) {
          console.warn('Unable to get user ID from storage', error);
        }
      }

      // Create JSON payload for the request
      const payload = {
        chat_id: activeConversation || 'new-chat',
        message: userMessage,
        user_id: currentUserId || 'anonymous',
        session_id: sessionId,
        timestamp: new Date().toISOString()
      };
      
      console.log('Sending webhook payload with user ID:', payload.user_id);
      
      // Send the request to the webhook
      const response = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Response from webhook:", data);
      
      return {
        role: "assistant",
        content: data.output || t('chatbot.errors.noResponse'),
      };
    } catch (error) {
      console.error("Error sending message to webhook:", error);
      toast({
        title: t('chatbot.errors.connectionError'),
        description: t('chatbot.errors.failedToConnect'),
        variant: "destructive",
      });

      // Return a fallback response in case of an error
      return {
        role: "assistant",
        content: t('chatbot.errors.somethingWentWrong'),
      };
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handleHideTabs = (hide: boolean) => {
    setHideBottomTabs(hide);
  };

  const handleStartNewConversation = () => {
    setShouldStartNewConversation(true);
    setActiveTab("messaging");
    // Reset active conversation when starting a new one
    setActiveConversation(null);
    setInitialMessage(null);
  };

  const handleStartConversationWithMessage = (message: string) => {
    setInitialMessage(message);
    setShouldStartNewConversation(true);
    setActiveTab("messaging");
    // Reset active conversation when starting a new one
    setActiveConversation(null);
  };

  const handleConversationStarted = () => {
    setShouldStartNewConversation(false);
    setInitialMessage(null);
  };

  return (
    <div>
      {/* Floating Action Button (FAB) */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            className="fixed bottom-20 md:bottom-5 right-5 bg-gradient-to-r from-gebeya-pink to-gebeya-orange rounded-full p-4 shadow-lg hover:shadow-xl z-50 flex items-center justify-center transition-transform hover:scale-105"
            aria-label={t('chatbot.openChat')}
          >
            <MessageSquare className="text-white" size={24} />
          </button>
        </SheetTrigger>
        <CustomPortal isOpen={isOpen}>
          <div className="fixed inset-0 z-50 bg-black/30" aria-hidden="true" onClick={() => setIsOpen(false)} />
          <div 
            className={`fixed z-50 bottom-[100px] right-0 w-full max-w-[400px] sm:max-w-[600px] lg:max-w-[380px] lg:mr-8 h-[70vh] sm:h-[600px] overflow-hidden ${isOpen ? 'slide-in-bottom' : 'slide-out-bottom'}`}
          >
            <div className="flex flex-col h-full bg-white rounded-t-lg shadow-xl overflow-hidden border-0">
              <Header showBackButton={activeTab === "messaging"} onBack={() => {
                setActiveTab("home");
                setActiveConversation(null);
              }} />
              {/* Main Content Area */}
              <div className="flex-1 relative min-h-0">
                <Tabs 
                  defaultValue="home" 
                  value={activeTab} 
                  onValueChange={handleTabChange} 
                  className="w-full h-full flex-1 min-h-0 flex flex-col"
                >
                  <div className="flex-1 min-h-0 flex flex-col">
                    <TabsContent value="home" className="flex-1 h-full min-h-0 m-0 focus-visible:outline-none focus-visible:ring-0">
                      <HomeTab 
                        userName={userName} 
                        hideTabsWhenDetail={handleHideTabs}
                        onStartNewConversation={handleStartNewConversation}
                        onStartConversationWithMessage={handleStartConversationWithMessage}
                      />
                    </TabsContent>
                    <TabsContent value="messaging" className="flex-1 h-full min-h-0 m-0 focus-visible:outline-none focus-visible:ring-0">
                      <MessagingTab 
                        userName={userName} 
                        hideTabsWhenDetail={handleHideTabs}
                        shouldStartNewConversation={shouldStartNewConversation}
                        initialMessage={initialMessage}
                        onConversationStarted={handleConversationStarted}
                        conversations={conversations}
                        activeConversation={activeConversation}
                        setActiveConversation={setActiveConversation}
                        updateConversation={updateConversation}
                        createNewConversation={createNewConversation}
                        sendMessageToWebhook={sendMessageToWebhook}
                      />
                    </TabsContent>
                  </div>
                  
                  {/* Tab navigation fixed at bottom */}
                  {!hideBottomTabs && (
                    <div className="border-t pb-4 sm:pb-6 mt-auto absolute bottom-0 left-0 right-0 bg-white z-10">
                      <TabsList className="w-full grid grid-cols-2 mt-2">
                        <TabsTrigger value="home" className="flex items-center gap-2 py-2 sm:py-3">
                          <Home size={18} /> 
                          <span className="text-sm sm:text-base">{t('chatbot.home')}</span>
                        </TabsTrigger>
                        <TabsTrigger value="messaging" className="flex items-center gap-2 py-2 sm:py-3">
                          <MessageSquare size={18} />
                          <span className="text-sm sm:text-base">{t('chatbot.messaging')}</span>
                        </TabsTrigger>
                      </TabsList>
                    </div>
                  )}
                </Tabs>
              </div>
            </div>
          </div>
        </CustomPortal>
      </Sheet>
    </div>
  );
};

export default JChatBot; 